import {
  Square,
  Heading,
  Stack,
  Row,
  Column,
  Copy,
  Button,
  Flex,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import classnames from 'classnames';
import { useParams } from 'react-router-dom';
import {
  useDepartmentSearch,
  ISearchParams,
  SearchStatus,
} from 'src/utilities/useSearchHooks';
import { Pagination } from 'src/components/Pagination/Pagination';
import { Input } from 'src/components/Input/Input';
import { sdk } from 'src/utilities/sdk';
import { pushSuccess, pushError } from 'src/utilities/notifications';
import Table from 'src/components/Table/Table';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { getHomeBreadcrumb } from 'src/constants/urls';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import { RouterLink } from 'src/components/RouterLink/RouterLink';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';

interface SearchFormFields {
  departmentQuery: string;
}

// LSS (Elastic Search) delay to propagate index changes post-DELETE
export const LSS_REINDEXING_DELAY = 250;

export default function EnterpriseDepartments(): JSX.Element {
  const { corpid } = useParams<{ corpid: string }>();
  const { register, handleSubmit } = useForm<SearchFormFields>();
  const [deletingState, setDeletingState] = useState({
    isInProgress: false,
    idBeingDeleted: null,
    nameBeingDeleted: null,
  });
  const currentQueryText = useRef(null);
  const nameColumnWidth = deletingState.isInProgress ? 5 : 7;
  const numberColumnWidth = deletingState.isInProgress ? 4 : 4;
  const actionColumnWidth = deletingState.isInProgress ? 3 : 1;
  const { corpData } = useAuth();
  const isDepartmentRequired = corpData.departmentsRequired === true;

  const {
    doSearch,
    resultsList: departments,
    currentSearchPage,
    hasNoResultsWithQuery,
    firstItemNumberFromPage,
    lastItemNumberFromPage,
    searchStatus,
    totalPages,
    totalResults,
  } = useDepartmentSearch(corpid);

  useEffect(() => {
    if (corpid) {
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid]);

  function searchDepartments({ departmentQuery }: SearchFormFields): void {
    const searchParams: ISearchParams = { page: 1 };

    if (departmentQuery) {
      searchParams.query = departmentQuery;
      currentQueryText.current = departmentQuery;
    } else {
      currentQueryText.current = null;
    }

    doSearch(searchParams);
  }

  function resetDeletingState(): void {
    setDeletingState({
      isInProgress: false,
      idBeingDeleted: null,
      nameBeingDeleted: null,
    });
  }

  function canDeleteDepartment(employeeCount: number): boolean {
    return (
      !isDepartmentRequired || (isDepartmentRequired && employeeCount === 0)
    );
  }

  async function deleteDepartment(): Promise<void> {
    const { idBeingDeleted } = deletingState;
    return sdk.http.delete(
      `/corp-admin/${corpid}/departments/${idBeingDeleted}`
    );
  }

  function handleSuccessfulDepartmentDelete(nameBeingDeleted: string): void {
    setTimeout(() => {
      pushSuccess(`${nameBeingDeleted} has been removed from your departments`);
      resetDeletingState();
      refreshDepartmentList();
    }, LSS_REINDEXING_DELAY);
  }

  function handleFailedDepartmentDelete(error, nameBeingDeleted: string): void {
    pushError(
      `${nameBeingDeleted} was not removed from your departments. Try again.`
    );
    if (error.status === '404') {
      refreshDepartmentList();
    }
    resetDeletingState();
  }

  function refreshDepartmentList(): void {
    const searchParams: ISearchParams = { page: 1 };
    doSearch(searchParams);
  }

  async function handleDeleteDepartment(): Promise<void> {
    const { nameBeingDeleted } = deletingState;

    try {
      await deleteDepartment();
      handleSuccessfulDepartmentDelete(nameBeingDeleted);
    } catch (error) {
      handleFailedDepartmentDelete(error, nameBeingDeleted);
    }
  }

  return (
    <Square inset="lg">
      <PageHeader>
        <Heading variant="h1">Departments</Heading>
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Admin home',
              ...getHomeBreadcrumb(corpid),
            },
          ]}
        />
      </PageHeader>
      <Stack y="md">
        <PageSection>
          <Row gutter={0}>
            <Column desktop={9} tablet={8} gutter={0}>
              <Heading variant="h4">Departments</Heading>
              <Copy variant="body">
                View all your departments, create departments, and add employees
                to a department.
              </Copy>
            </Column>
            <Column
              desktop={3}
              tablet={4}
              gutter={0}
              style={{ textAlign: 'center' }}
            >
              <RouterButtonLink
                href={`/corporate/enterprise/${corpid}/departments/create`}
                buttonVariant="primary"
                useBlockButton
                buttonSize="medium"
              >
                Add a New Department
              </RouterButtonLink>
            </Column>
          </Row>
        </PageSection>
      </Stack>
      <PageSection>
        <Heading variant="h4">Active Departments</Heading>
        <Stack y="x-sm">
          <Copy variant="body">
            {firstItemNumberFromPage} - {lastItemNumberFromPage} of{' '}
            {totalResults} Departments
          </Copy>
        </Stack>
        <Stack y="md" as="form">
          <Row gutter={0}>
            <Column gutter={0} tablet={6}>
              <Row gutter={8}>
                <Column gutter={8} tablet={8}>
                  <Input
                    ariaLabel="Search departments"
                    name="departmentQuery"
                    data-testid="departmentQuery"
                    id="departmentQuery"
                    ref={register}
                    placeholder="Department Name"
                  />
                </Column>
                <Column gutter={8} tablet={4}>
                  <Button
                    variant="primary"
                    block
                    buttonSize="medium"
                    onClick={handleSubmit(searchDepartments)}
                  >
                    Search
                  </Button>
                </Column>
              </Row>
            </Column>
          </Row>
        </Stack>
        <Stack y="md">
          <Table
            isLoading={searchStatus === SearchStatus.LOADING}
            header={() => (
              <Row gutter={0}>
                <Column gutter={0} phone={nameColumnWidth}>
                  <Copy variant="body" color="secondaryText">
                    Name
                  </Copy>
                </Column>
                <Column gutter={0} phone={numberColumnWidth}>
                  <Copy variant="body" color="secondaryText">
                    Number of Employees
                  </Copy>
                </Column>
                <Column gutter={0} phone={actionColumnWidth}>
                  <Copy variant="body" color="secondaryText">
                    Actions
                  </Copy>
                </Column>
              </Row>
            )}
            body={() =>
              totalResults > 0 ? (
                <ul data-testid="department-list" className="removeListStyles">
                  {departments.map((department) => (
                    <Square
                      as="li"
                      data-testid={`department-row-${department.id}`}
                      key={department.id}
                      inset="md"
                      borderBottom="1px solid"
                      borderColor="lightLine"
                    >
                      <Row gutter={0}>
                        <Column
                          gutter={0}
                          phone={nameColumnWidth}
                          className="centeredFlexColumn"
                        >
                          <RouterLink
                            href={`/corporate/enterprise/${corpid}/departments/${department.id}`}
                          >
                            {department.name}
                          </RouterLink>
                        </Column>
                        <Column
                          gutter={0}
                          phone={numberColumnWidth}
                          className="centeredFlexColumn"
                        >
                          <Copy variant="body">
                            {department.employee_count}
                          </Copy>
                        </Column>
                        <Column
                          gutter={0}
                          phone={actionColumnWidth}
                          className="centeredFlexColumn"
                        >
                          {deletingState.idBeingDeleted !== department.id &&
                            department.name !== 'Unassigned' && (
                              <Tooltip
                                roundedContainer
                                disabled={canDeleteDepartment(
                                  department.employee_count
                                )}
                                content={
                                  <Copy
                                    variant="body"
                                    color="invertedPrimaryText"
                                  >
                                    Departments with employees cannot be deleted
                                  </Copy>
                                }
                              >
                                <div>
                                  <Button
                                    variant="tertiary"
                                    buttonSize="medium"
                                    className={classnames(
                                      'removeButtonPadding',
                                      'autoButtonHeight'
                                    )}
                                    disabled={
                                      deletingState.isInProgress ||
                                      !canDeleteDepartment(
                                        department.employee_count
                                      )
                                    }
                                    onClick={() => {
                                      setDeletingState({
                                        isInProgress: true,
                                        idBeingDeleted: department.id,
                                        nameBeingDeleted: department.name,
                                      });
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </Tooltip>
                            )}
                          {deletingState.idBeingDeleted === department.id && (
                            <Flex alignItems="center">
                              <Stack x="x-sm">
                                <Copy variant="body">Delete department?</Copy>
                              </Stack>
                              <Stack x="x-sm">
                                <Button
                                  variant="primary"
                                  onClick={handleDeleteDepartment}
                                >
                                  Yes
                                </Button>
                              </Stack>
                              <Button
                                variant="secondary"
                                onClick={() => {
                                  resetDeletingState();
                                }}
                              >
                                No
                              </Button>
                            </Flex>
                          )}
                        </Column>
                      </Row>
                    </Square>
                  ))}
                </ul>
              ) : (
                hasNoResultsWithQuery && (
                  <NoResultsMsg
                    message={`No results matching '${currentQueryText.current}'`}
                  />
                )
              )
            }
            showFooter={false}
          />
        </Stack>
        {totalPages > 1 && (
          <Flex justifyContent="center">
            <Pagination
              boundaryLinks
              page={currentSearchPage}
              collectionSize={totalResults}
              pageChange={(page) => {
                const searchParams: ISearchParams = { page };

                if (currentQueryText.current) {
                  searchParams.query = currentQueryText.current;
                }

                doSearch(searchParams);
              }}
            />
          </Flex>
        )}
      </PageSection>
    </Square>
  );
}
